// Search drawer: cardo search (in the browser, typos forgiven) drawn as the
// theme's own product cards. Data: /collections/all?view=search-data
// (templates/collection.search-data.liquid) - products, menu pages and blog
// posts, loaded the first time the shopper heads for the search box.
// "See all" goes to Shopify's full /search page, as before.
import { bindSearch, loadEntries } from '@split-infinity/cardo/search'

const DATA = '/collections/all?view=search-data'
const PRODUCTS_SHOWN = 6
const OTHERS_SHOWN = 3

const qs = (selector, root = document) => root?.querySelector(selector) ?? null
let bound = false

function layer() {
  const panel = qs('[data-search-panel]')
  const results = qs('[data-search-results]')
  const resultsLayer = qs('[data-search-results-layer]')
  return { panel, results, resultsLayer }
}

// The results hang just under the search panel.
function placeResults() {
  const { panel, resultsLayer } = layer()
  if (!panel || !resultsLayer) return
  resultsLayer.style.top = Math.max(0, panel.getBoundingClientRect().bottom) + 'px'
}

function show(on) {
  const { panel, results } = layer()
  results?.classList.toggle('hidden', !on)
  // Softer panel shadow while results show
  panel?.classList.toggle('shadow-xl/100', !on)
  panel?.classList.toggle('shadow-lg/50', on)
}

function render(matches, query) {
  const { results } = layer()
  if (!results) return
  if (query.length < 2) {
    results.replaceChildren()
    show(false)
    return
  }
  placeResults()
  const products = matches.filter((m) => m.entry.kind === 'product').slice(0, PRODUCTS_SHOWN)
  const others = matches.filter((m) => m.entry.kind !== 'product').slice(0, OTHERS_SHOWN)

  const box = document.createElement('div')
  box.className = 'gap-xs-s flex flex-col'
  if (!matches.length) {
    const none = document.createElement('div')
    none.className = 'text-black/70'
    none.textContent = `No results found for “${query}”`
    box.append(none)
  }
  if (products.length) {
    const grid = document.createElement('div')
    grid.className = 'gap-2xs mini:grid-cols-3 grid grid-cols-2'
    grid.innerHTML = products.map((m) => m.entry.html).join('')
    box.append(grid)
  }
  if (others.length) {
    const list = document.createElement('ul')
    list.className = 'gap-3xs flex flex-col'
    for (const { entry } of others) {
      const li = document.createElement('li')
      const a = document.createElement('a')
      a.href = entry.url
      a.className = 'font-merriweather text-0 text-black underline-offset-2 hover:underline'
      a.textContent = entry.title
      li.append(a)
      list.append(li)
    }
    box.append(list)
  }
  if (matches.length) {
    const all = document.createElement('div')
    all.className = 'flex justify-end'
    const a = document.createElement('a')
    a.className = 'btn-secondary px-xs py-2xs'
    a.href = `/search?${new URLSearchParams({ q: query, type: 'product,page,article', 'options[prefix]': 'last' })}`
    a.textContent = `See all ${matches.length > 99 ? '99+' : matches.length} results`
    all.append(a)
    box.append(all)
  }
  results.replaceChildren(box)
  window.hydrateMediaImages?.(results)
  show(true)
}

function start() {
  const { panel, results } = layer()
  const input = qs('[data-search-input]', panel)
  if (!input || !results || bound) return
  bound = true
  bindSearch({ input, results, url: DATA, render })
}

document.addEventListener('cardo:drawer', (e) => {
  if (e.detail.name !== 'search') return
  const { panel, results } = layer()
  const input = qs('[data-search-input]', panel)
  if (e.detail.open) {
    start()
    input?.focus()
    placeResults()
    window.addEventListener('resize', placeResults)
    document.addEventListener('scroll', placeResults, true)
  } else {
    window.removeEventListener('resize', placeResults)
    document.removeEventListener('scroll', placeResults, true)
    if (input) input.value = ''
    results?.replaceChildren()
    show(false)
  }
})

// The search icon is where shoppers head first: start loading on hover.
document.addEventListener('pointerover', (e) => {
  if (e.target.closest?.('[data-drawer="search"]')) loadEntries(DATA).catch(() => {})
})
